/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes, useMemo } from 'preact/compat';
import { classes, getPriceFormatter } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/Price/Price.css';

export interface PriceProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'size'> {
  amount?: number;
  currency?: string | null;
  locale?: string;
  formatOptions?: {
    [key: string]: any;
  };
  variant?: 'default' | 'strikethrough';
  weight?: 'bold' | 'normal';
  sale?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const Price: FunctionComponent<PriceProps> = ({
  amount = 0,
  currency,
  locale,
  variant = 'default',
  weight = 'bold',
  className,
  children,
  sale = false,
  formatOptions = {},
  size = 'small',
  ...props
}) => {
  const formattedAmount = useMemo(() => {
    const formatter = getPriceFormatter({ currency, locale, formatOptions });
    return formatter.format(amount);
  }, [amount, currency, locale, formatOptions]);

  return (
    <span
      {...props}
      className={classes([
        'dropin-price',
        `dropin-price--${variant}`,
        `dropin-price--${size}`,
        `dropin-price--${weight}`,
        ['dropin-price--sale', sale],
        className,
      ])}
    >
      {formattedAmount}
    </span>
  );
};
